import { StatCard } from '../StatCard';
import { useWidgetData, aggregate, accentMap, currencyFmt } from './shared';
import type { DashboardWidget } from '../../types/panelConfig';

interface StatWidgetProps {
  widget: Extract<DashboardWidget, { type: 'stat' }>;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

const numberFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });

function fmt(n: number, format: 'currency' | 'number' | undefined): string {
  return format === 'number' ? numberFmt.format(n) : currencyFmt.format(n);
}

export function StatWidget({ widget, sourceId, parameters }: StatWidgetProps) {
  const { data, isLoading } = useWidgetData(sourceId, `stat-${widget.reportCode}`, parameters);
  const { statField, label, aggregation, color, format, ratio } = widget.config;

  const rows = data ?? [];
  let value: string;
  if (rows.length === 0) {
    value = '—';
  } else if (ratio) {
    // Ratio always sums both fields regardless of `aggregation`.
    const den = aggregate(rows, ratio.denField, 'sum');
    value = den ? fmt(aggregate(rows, ratio.numField, 'sum') / den, format) : '—';
  } else if (!statField) {
    // Misconfigured widget (neither ratio nor statField) — show placeholder, not a misleading 0.
    value = '—';
  } else {
    value = fmt(aggregate(rows, statField, aggregation ?? 'sum'), format);
  }

  return (
    <div data-testid="stat-widget">
      <StatCard
        title={label}
        value={value}
        accentColor={accentMap[color || 'blue'] ?? accentMap.blue}
        loading={isLoading}
      />
    </div>
  );
}
